"use client";
import "./jobList.css";
import "../testimonials/testimonials.css";
import {
  Heading,
  Box,
  Badge,
  SkeletonCircle,
  SkeletonText,
  GridItem,
  Flex,
  Image,
  Text,
  VStack,
  useToast,
  useColorModeValue,
  Select,
  InputGroup,
  Input,
  InputLeftElement,
  InputRightElement,
  Button,
  Tag,
  TagLabel,
  TagCloseButton,
  FormControl,
  Wrap,
  WrapItem,
  SimpleGrid,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormLabel,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { updateBookmarkAsync } from "../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";
import {
  fetchPublicJobListAsync,
  selectPublicJobList,
  selectPublicJobListTotal,
  selectCurrentFilters,
  clearFilters,
} from "./publicJobListSlice/publicJobListSlice";
import { fetchSuggestionsAsync, selectSuggestions } from "../jobSearch/Suggestions/SuggestionsSlice";
import { fetchPublicCoreLocationsAsync, selectPublicCoreLocations } from "../jobSearch/FetchLocationSlice/FetchLocationSlice";
import {
  BriefcaseIcon,
  CurrencyRupeeIcon,
  BuildingOffice2Icon,
  ClockIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  MapPinIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../common/URLs";
import { TimeDifference } from "../common/TimeDifference";
import companyLogo from "./logo1png.png";
import { NoDataFound } from "../../pages/CommonPublicPages/Basic/NoDataFound";
import { fetchUserRole } from "../common/FetchUserRole";
import { Footer } from "../footer/footer";
import { FaFilter } from "react-icons/fa";
import { fetchJobCategoryListAsync, selectJobCategoryList } from "../jobCategories/JobCategorySlice/JobCategorySlice";

const JOBLIST_PERSIST_KEY = "jobListPersist";

export function JobList() {
  const dispatch = useDispatch();
  const location = useLocation();
  const jobs = useSelector(selectPublicJobList);
  const totalJobs = useSelector(selectPublicJobListTotal);
  const currentFilters = useSelector(selectCurrentFilters);
  const suggestions = useSelector(selectSuggestions);
  const coreLocation = useSelector(selectPublicCoreLocations);
  const coreLocationLoading = useSelector((state) => state.publicCoreLocations?.loading);
  const jobCategories = useSelector(selectJobCategoryList);
  const jobCategoriesLoading = useSelector((state) => state.jobCategoryList?.loading);
  const toast = useToast();
  const navigate = useNavigate();

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [experience, setExperience] = useState("");
  const [locations, setLocations] = useState([]);
  const [jobType, setJobType] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [salaryRange, setSalaryRange] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [bookmarkedJobs, setBookmarkedJobs] = useState({});
  const [appliedJobs, setAppliedJobs] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [departmentUuid, setDepartmentUuid] = useState("");

  // Location label map for displaying names
  const [locationLabelMap, setLocationLabelMap] = useState({});

  // Location dropdown search
  const [isLocMenuOpen, setIsLocMenuOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");

  // Refs
  const suggestionsRef = useRef(null);
  const hasFetchedLocations = useRef(false);
  const isInitialLoadComplete = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Constants
  const jobsPerPage = 16;
  const totalPages = Math.max(1, Math.ceil(((totalJobs || 0)) / jobsPerPage));
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const headerTextColor = useColorModeValue("white", "gray.100");
  const subTextColor = useColorModeValue("blue.100", "blue.200");
  const cardBg = useColorModeValue("white", "gray.800");

  // Derived flags
  useEffect(() => {
    const activeFilters =
      selectedSuggestions.length > 0 ||
      locations.length > 0 ||
      experience ||
      jobType ||
      salaryRange !== "" ||
      departmentUuid ||
      keyword.trim() !== "";
    setHasActiveFilters(activeFilters);
  }, [selectedSuggestions, locations, experience, jobType, salaryRange, departmentUuid, keyword]);

  // Click outside keyword suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper to compute uuids from selected suggestions
  const getFilterUuids = useCallback((suggestionsList) => {
    const jsUuids = suggestionsList.filter((i) => i.uuid?.startsWith("js")).map((i) => i.uuid.replace(/^js_/, ""));
    const jtUuids = suggestionsList.filter((i) => i.uuid?.startsWith("jt")).map((i) => i.uuid.replace(/^jt_/, ""));
    const cnUuids = suggestionsList.filter((i) => i.uuid?.startsWith("cn")).map((i) => i.uuid.replace(/^cn_/, ""));
    const catUuids = suggestionsList.filter((i) => i.uuid?.startsWith("jc")).map((i) => i.uuid.replace(/^jc_/, ""));
    return { jsUuids, jtUuids, cnUuids, catUuids };
  }, []);

  // Build payload helper
  const buildPayload = useCallback((stateObj) => {
    const { jsUuids, jtUuids, cnUuids, catUuids } = getFilterUuids(stateObj.selectedSuggestions || []);

    return {
      page: stateObj.currentPage || 1,
      perPageCount: jobsPerPage,
      sort_by: stateObj.sortBy || "newest",
      ...(stateObj.experience && { experience: stateObj.experience }),
      ...(stateObj.locations?.length && { locations: stateObj.locations }),
      ...(jsUuids.length && { skills: jsUuids }),
      ...(jtUuids.length && { titles: jtUuids }),
      ...(cnUuids.length && { company: cnUuids }),
      ...(catUuids.length && { categories: catUuids }),
      ...(stateObj.jobType && { job_type: stateObj.jobType }),
      ...(stateObj.salaryRange && { salary_range: stateObj.salaryRange }),
      ...(stateObj.departmentUuid && { department_uuid: stateObj.departmentUuid }),
    };
  }, [getFilterUuids, jobsPerPage]);

  // Initial data load - runs only once on mount
  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);

      try {
        // Fetch locations if not already loaded
        if (!hasFetchedLocations.current && (!coreLocation || coreLocation.length === 0)) {
          hasFetchedLocations.current = true;
          dispatch(fetchPublicCoreLocationsAsync());
        }

        // Fetch job categories
        dispatch(fetchJobCategoryListAsync());

        // Determine initial state
        const cameFromJobSearch = Boolean(location.state?.fromJobSearch);
        let persisted = null;

        // Try to restore from session storage
        if (!cameFromJobSearch) {
          try {
            const raw = sessionStorage.getItem(JOBLIST_PERSIST_KEY);
            if (raw) persisted = JSON.parse(raw);
          } catch (e) {
            console.error("Error parsing persisted state:", e);
          }
        }

        let stateToApply;

        if (!cameFromJobSearch && persisted) {
          // Restore from persisted state (back navigation)
          stateToApply = persisted;
        } else if (location.state) {
          // Initial navigation from JobSearch or Navbar with explicit state
          stateToApply = {
            keyword: location.state.keyword ?? "",
            selectedSuggestions: location.state.selectedSuggestions ?? [],
            experience: location.state.experience ?? "",
            locations: location.state.selectedLocations ?? [],
            jobType: location.state.jobType ?? "",
            sortBy: "newest",
            salaryRange: "",
            currentPage: 1,
            locationLabelMap: location.state.locationLabelMap ?? {},
            departmentUuid: location.state.departmentUuid ?? "",
          };
        } else {
          // Default state
          stateToApply = {
            keyword: "",
            selectedSuggestions: [],
            experience: "",
            locations: [],
            jobType: "",
            sortBy: "newest",
            salaryRange: "",
            currentPage: 1,
            locationLabelMap: {},
            departmentUuid: "",
          };
        }

        // Apply state
        setKeyword(stateToApply.keyword);
        setSelectedSuggestions(stateToApply.selectedSuggestions);
        setExperience(stateToApply.experience);
        setLocations(stateToApply.locations);
        setJobType(stateToApply.jobType);
        setSortBy(stateToApply.sortBy);
        setSalaryRange(stateToApply.salaryRange);
        setCurrentPage(stateToApply.currentPage);
        setLocationLabelMap(stateToApply.locationLabelMap);
        setDepartmentUuid(stateToApply.departmentUuid);

        // Build and execute the API call
        const payload = buildPayload(stateToApply);
        await dispatch(fetchPublicJobListAsync(payload));

      } catch (error) {
        console.error("Initial data loading error:", error);
        toast({
          title: "Error loading data",
          description: "Could not fetch job listings",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        setIsLoading(false);
        // Mark initial load as complete after state is settled
        isInitialLoadComplete.current = true;
      }
    };

    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only once on mount

  // Persist state to sessionStorage whenever filters or page change
  useEffect(() => {
    if (!isInitialLoadComplete.current) return;

    const toPersist = {
      keyword,
      selectedSuggestions,
      experience,
      locations,
      jobType,
      sortBy,
      salaryRange,
      currentPage,
      locationLabelMap,
      departmentUuid,
    };
    try {
      sessionStorage.setItem(JOBLIST_PERSIST_KEY, JSON.stringify(toPersist));
    } catch (e) {
      console.error("Error persisting state:", e);
    }
  }, [keyword, selectedSuggestions, experience, locations, jobType, sortBy, salaryRange, currentPage, locationLabelMap, departmentUuid]);

  // Initialize applied jobs and bookmarks
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {};
      const initialBookmarks = {};

      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1;
        initialBookmarks[job.uuid] = job.candidate_job_saved_count > 0;
      });

      setAppliedJobs(initialAppliedJobs);
      setBookmarkedJobs(initialBookmarks);
    }
  }, [jobs]);

  // Debounced suggestions fetch for keyword
  useEffect(() => {
    const timer = setTimeout(() => {
      if (keyword.trim()) {
        dispatch(fetchSuggestionsAsync({ keyword: keyword.trim() }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword, dispatch]);

  // Debounced location fetch for dropdown keyword
  useEffect(() => {
    if (!isLocMenuOpen) return;
    const term = locationSearch.trim();
    const t = setTimeout(() => {
      dispatch(fetchPublicCoreLocationsAsync({ keyword: term }));
    }, 300);
    return () => clearTimeout(t);
  }, [locationSearch, isLocMenuOpen, dispatch]);

  // Search handler - centralized function for fetching jobs
  const executeSearch = useCallback(async (page = 1) => {
    const stateObj = {
      selectedSuggestions,
      experience,
      locations,
      jobType,
      salaryRange,
      departmentUuid,
      sortBy,
      currentPage: page,
    };

    const payload = buildPayload(stateObj);

    setIsLoading(true);
    try {
      await dispatch(fetchPublicJobListAsync(payload));
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "Search failed",
        description: "Could not fetch job listings",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
      setIsInputFocused(false);
      setShowSuggestions(false);
    }
  }, [selectedSuggestions, experience, locations, jobType, salaryRange, departmentUuid, sortBy, buildPayload, dispatch, toast]);

  // Effect to trigger search when filters change (after initial load)
  useEffect(() => {
    if (!isInitialLoadComplete.current) return;

    const timer = setTimeout(() => {
      executeSearch(currentPage);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuggestions, locations, departmentUuid, jobType, salaryRange, sortBy]);

  // Separate effect for experience to reset to page 1
  useEffect(() => {
    if (!isInitialLoadComplete.current) return;

    const timer = setTimeout(() => {
      setCurrentPage(1);
      executeSearch(1);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience]);

  // Helpers for suggestion badges
  const getSuggestionType = (uuid) => {
    if (uuid.startsWith("js")) return "Skill";
    if (uuid.startsWith("jt")) return "Job Title";
    if (uuid.startsWith("cn")) return "Company";
    if (uuid.startsWith("jc")) return "Category";
    return "Other";
  };

  const getSuggestionColorScheme = (uuid) => {
    if (uuid.startsWith("js")) return "blue";
    if (uuid.startsWith("jt")) return "green";
    if (uuid.startsWith("cn")) return "purple";
    if (uuid.startsWith("jc")) return "orange";
    return "gray";
  };

  // Manual search button handler
  const handleSearchClick = useCallback(() => {
    setCurrentPage(1);
    executeSearch(1);
  }, [executeSearch]);

  const handleChange = (e) => {
    setKeyword(e.target.value);
    if (e.target.value.trim()) setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    if (!selectedSuggestions.some((item) => item.uuid === suggestion.uuid)) {
      setSelectedSuggestions([...selectedSuggestions, suggestion]);
      setCurrentPage(1);
    }
    setKeyword("");
    setShowSuggestions(false);
  };

  const handleDepartmentChange = (e) => {
    const value = e.target.value;
    setDepartmentUuid(value);
    setCurrentPage(1);
  };

  const removeDepartment = () => {
    setDepartmentUuid("");
    setCurrentPage(1);
  };

  const handleSelect = (suggestion) => {
    if (!selectedSuggestions.some((item) => item.uuid === suggestion.uuid)) {
      setSelectedSuggestions([...selectedSuggestions, suggestion]);
      setCurrentPage(1);
    }
  };

  const handleLocationSelect = (e) => {
    const selectedLocation = e.target.value;
    if (selectedLocation && !locations.includes(selectedLocation)) {
      setLocations([...locations, selectedLocation]);
      if (Array.isArray(coreLocation)) {
        const found = coreLocation.find((item) => item.uuid === selectedLocation);
        if (found?.title) {
          setLocationLabelMap((prev) => ({ ...prev, [selectedLocation]: found.title }));
        }
      }
      setCurrentPage(1);
    }
  };

  const handleLocationClick = (uuid) => {
    if (Array.isArray(coreLocation)) {
      const found = coreLocation.find((item) => item.uuid === uuid);
      if (found?.title) {
        setLocationLabelMap((prev) => ({ ...prev, [uuid]: found.title }));
      }
    }
    handleLocationSelect({ target: { value: uuid } });
  };

  const handleExperienceChange = (e) => {
    setExperience(e.target.value);
    // Page reset is handled in the experience useEffect
  };

  const handleJobTypeChange = (e) => {
    setJobType(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const handleSalaryRangeChange = (e) => {
    setSalaryRange(e.target.value);
    setCurrentPage(1);
  };

  const removeLocation = (locUuid) => {
    setLocations(locations.filter((loc) => loc !== locUuid));
    setCurrentPage(1);
  };

  const removeSuggestion = (suggestionUuid) => {
    setSelectedSuggestions(selectedSuggestions.filter((s) => s.uuid !== suggestionUuid));
    setCurrentPage(1);
  };

  const clearAllFilters = useCallback(() => {
    setKeyword("");
    setSelectedSuggestions([]);
    setExperience("");
    setLocations([]);
    setLocationLabelMap({});
    setJobType("");
    setSalaryRange("");
    setSortBy("newest");
    setCurrentPage(1);
    setDepartmentUuid("");
    setShowSuggestions(false);

    try {
      sessionStorage.removeItem(JOBLIST_PERSIST_KEY);
    } catch (e) {
      console.error("Error removing persisted state:", e);
    }

    dispatch(clearFilters());

    setIsLoading(true);
    dispatch(
      fetchPublicJobListAsync({
        page: 1,
        perPageCount: jobsPerPage,
        sort_by: "newest",
      })
    )
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [dispatch, jobsPerPage]);

  const handleBookmarkClick = async (jobId) => {
    try {
      const role = await new Promise((resolve) => {
        fetchUserRole((role) => resolve(role));
      });

      if (role !== "candidate") {
        toast({
          title: "Please login first",
          description: "You need to login as a candidate to save or apply for a job.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        navigate("/login");
        return;
      }

      const isCurrentlyBookmarked = bookmarkedJobs[jobId];
      const newStatus = isCurrentlyBookmarked ? "remove" : "save";

      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: !isCurrentlyBookmarked,
      }));

      await dispatch(updateBookmarkAsync({ job_uuid: jobId, status: newStatus }));

      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"} successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);
      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: !prev[jobId],
      }));

      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      executeSearch(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      executeSearch(prevPage);
    }
  };

  // Function to render job content
  const renderJobContent = () => {
    if (isLoading) {
      return Array.from({ length: 4 }).map((_, index) => (
        <GridItem key={index}>
          <Box borderWidth="1px" borderRadius="lg" p={6} bg="white" boxShadow="sm" transition="all 0.2s" _hover={{ boxShadow: "md" }}>
            <Flex>
              <SkeletonCircle size="60px" mr={4} />
              <Box flex={1}>
                <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="2" />
              </Box>
            </Flex>
          </Box>
        </GridItem>
      ));
    } else if (!Array.isArray(jobs) || jobs.length === 0) {
      return (
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <div>
            <NoDataFound
              title="No Job Listings Found"
              message="We couldn't find any job listings matching your criteria. Please try different filters or search terms."
            />
          </div>
        </GridItem>
      );
    } else {
      return jobs.map((job) => (
        <GridItem key={job.uuid}>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            bg="white"
            _hover={{ transform: "translateY(-2px)" }}
            transition="all 0.2s"
            overflow="hidden"
            boxShadow="sm"
          >
            <Flex p={6} className="flex-wrap">
              <Box flexShrink={0} mr={4}>
                <Image
                  src={job.company_logo ? `${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}` : companyLogo}
                  alt={job.name || "Company"}
                  className="h-16 w-16 object-contain rounded-full border"
                  onError={(e) => {
                    e.target.src = companyLogo;
                  }}
                />
              </Box>

              <Box flex={1}>
                <Link to={`/jobs/${job.uuid}`}>
                  <div>
                    <div className="font-semibold text-lg leading-tight break-words">
                      {job?.job_title?.title ? job?.job_title?.title : job.job_name}
                      <Badge ml={"1"} colorScheme="telegram" variant="subtle" fontSize="0.7rem" verticalAlign="middle">
                        {job.job_tag}
                      </Badge>

                      {job.urgent_hiring_tag_enable == 1 && (
                        <Badge
                          ml="1"
                          colorScheme="red"
                          variant="subtle"
                          fontSize="0.7rem"
                          verticalAlign="middle"
                        >
                          Urgent hiring
                        </Badge>
                      )}

                    </div>
                  </div>

                  <div className="text-blue-600 text-[0.95rem] font-medium mb-2">{job?.company_name || "Company name"}</div>

                  <div className="text-gray-600 text-sm mb-2 flex items-center gap-1">
                    <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
                    <span className="font-normal">{job?.job_name || "Job Name"}</span>
                  </div>

                  <div className="text-gray-700 text-sm mb-2 flex items-center gap-1">
                    <CurrencyRupeeIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">
                      {job.salary_min_amount === null || job.salary_min_amount === 0
                        ? "Not Disclosed"
                        : `₹${job.salary_min_amount || "N/A"} - ₹${job.salary_max_amount || "N/A"} / ${job.salary_per_type || "Not Specified"}`}
                    </span>
                  </div>

                  <div className="text-gray-600 text-sm mb-2 flex items-center gap-1">
                    <BriefcaseIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-normal">{job.job_type || "Job Type"}</span>
                  </div>

                  {/* Job Locations */}
                  <div className="text-gray-600 text-sm mb-2 flex items-center gap-1">
                    <MapPinIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-normal">
                      {job.job_locations && job.job_locations.length > 0 ? (
                        <>
                          {job.job_locations.slice(0, 4).map((loc) => loc.job_location?.title).join(", ")}
                          {job.job_locations.length > 4 && <> +{job.job_locations.length - 4} more</>}
                        </>
                      ) : (
                        "No locations"
                      )}
                    </span>
                  </div>

                  {/* Job Skills */}
                  <div className="text-gray-600 text-sm mb-2 flex items-center gap-1">
                    <SparklesIcon className="w-4 h-4 text-gray-500" />
                    <span className="font-normal ">
                      {job.job_skills && job.job_skills.length > 0 ? (
                        <>
                          {job.job_skills.slice(0, 2).map((skill) => skill?.job_skill?.title).join(", ")}
                          {job.job_skills.length > 3 && <> +{job.job_skills.length - 3} more</>}
                        </>
                      ) : (
                        "No skills"
                      )}
                    </span>
                  </div>

                  <div className="text-gray-500 text-xs font-normal flex items-center gap-1">
                    <ClockIcon className="w-4 h-4 text-gray-500" />
                    <TimeDifference dateTimeString={job.created_at} />
                  </div>
                </Link>
              </Box>

              <div className="flex flex-col items-end justify-between">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleBookmarkClick(job.uuid);
                  }}
                  className="bookmark"
                >
                  {bookmarkedJobs[job.uuid] ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z" stroke="black" strokeWidth="2" />
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z" stroke="black" strokeWidth="2" />
                    </svg>
                  )}
                </button>

                {appliedJobs[job.uuid] && <div className="text-green-600 text-[0.85rem] font-semibold flex items-center gap-1">Applied</div>}
              </div>
            </Flex>
          </Box>
        </GridItem>
      ));
    }
  };

  // Filter Sidebar Component
  const FilterSidebar = () => (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden" bg={cardBg} boxShadow="md" p={5} height="fit-content" position={{ base: "static", lg: "sticky" }} className="top-20">
      <Flex direction="column" gap={4}>
        <Flex justify="space-between" align="center" borderBottom="1px" borderColor="gray.200" pb={3}>
          <Text fontWeight="600" fontSize="md" color="gray.700">
            Filter Jobs
          </Text>
          {hasActiveFilters && (
            <Button size="sm" variant="ghost" colorScheme="blue" onClick={clearAllFilters} leftIcon={<FaFilter />}>
              Clear All
            </Button>
          )}
        </Flex>

        <VStack spacing={4} align="stretch">
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Sort By
            </Text>
            <Select value={sortBy} onChange={handleSortChange} size="md" bg="white" borderColor="gray.300" _hover={{ borderColor: "gray.400" }}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="salary_high">Salary High to Low</option>
              <option value="salary_low">Salary Low to High</option>
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Job Type
            </Text>
            <Select value={jobType} onChange={handleJobTypeChange} size="md" bg="white" borderColor="gray.300" _hover={{ borderColor: "gray.400" }}>
              <option value="">All Types</option>
              <option value="Full Time">Full Time</option>
              <option value="Part Time">Part Time</option>
              <option value="Contract">Contract</option>
              <option value="Freelance">Freelance</option>
              <option value="Internship">Internship</option>
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Department
            </Text>
            <Select
              value={departmentUuid}
              onChange={handleDepartmentChange}
              size="md"
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              placeholder={jobCategoriesLoading ? "Loading..." : "All Departments"}
              isDisabled={jobCategoriesLoading}
            >
              {Array.isArray(jobCategories) &&
                jobCategories.map((dept) => (
                  <option key={dept.uuid} value={dept.uuid}>
                    {dept?.title} {typeof dept.job_count === "number" ? `(${dept.job_count})` : ""}
                  </option>
                ))}
            </Select>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Salary Range
            </Text>
            <Select value={salaryRange} onChange={handleSalaryRangeChange} size="md" bg="white" borderColor="gray.300" _hover={{ borderColor: "gray.400" }}>
              <option value="">All Salary Ranges</option>
              <option value="30000-300000">₹30K - ₹3L</option>
              <option value="300000-600000">₹3L - ₹6L</option>
              <option value="600000-1000000">₹6L - ₹10L</option>
              <option value="1000000-2000000">₹10L - ₹20L</option>
              <option value="2000000-10000000">Above ₹20L</option>
            </Select>
          </Box>
        </VStack>

        {(selectedSuggestions.length > 0 || locations.length > 0 || departmentUuid) && (
          <Box mt={2}>
            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
              Active Filters:
            </Text>
            <Flex gap={2} flexWrap="wrap">
              {selectedSuggestions.map((suggestion) => (
                <Tag key={suggestion.uuid} size="md" borderRadius="full" variant="subtle" colorScheme={getSuggestionColorScheme(suggestion.uuid)} py={1} px={3}>
                  <TagLabel fontWeight="medium">
                    {suggestion.title} ({getSuggestionType(suggestion.uuid)})
                  </TagLabel>
                  <TagCloseButton onClick={() => removeSuggestion(suggestion.uuid)} />
                </Tag>
              ))}

              {locations.map((locUuid) => (
                <Tag key={locUuid} size="md" borderRadius="full" variant="subtle" colorScheme="orange" py={1} px={3}>
                  <TagLabel fontWeight="medium">
                    {locationLabelMap[locUuid] ||
                      (Array.isArray(coreLocation) && coreLocation.find((loc) => loc.uuid === locUuid)?.title) ||
                      locUuid}
                  </TagLabel>
                  <TagCloseButton onClick={() => removeLocation(locUuid)} />
                </Tag>
              ))}

              {departmentUuid && (
                <Tag size="md" borderRadius="full" variant="subtle" colorScheme="teal" py={1} px={3}>
                  <TagLabel fontWeight="medium">
                    {(Array.isArray(jobCategories) && jobCategories.find((d) => d.uuid === departmentUuid)?.title) || "Department"}
                  </TagLabel>
                  <TagCloseButton onClick={removeDepartment} />
                </Tag>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  );

  // Mobile filter drawer
  const FilterDrawer = () => (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Filter Jobs</DrawerHeader>
        <DrawerBody>
          <Stack spacing={4} mt={4}>
            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Sort By
              </FormLabel>
              <Select value={sortBy} onChange={handleSortChange} size="md" borderColor="gray.300">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="salary_high">Salary High to Low</option>
                <option value="salary_low">Salary Low to High</option>
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Job Type
              </FormLabel>
              <Select value={jobType} onChange={handleJobTypeChange} size="md" borderColor="gray.300">
                <option value="">All Types</option>
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Contract">Contract</option>
                <option value="Freelance">Freelance</option>
                <option value="Internship">Internship</option>
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Department
              </FormLabel>
              <Select
                value={departmentUuid}
                onChange={handleDepartmentChange}
                size="md"
                borderColor="gray.300"
                placeholder={jobCategoriesLoading ? "Loading..." : "All Departments"}
                isDisabled={jobCategoriesLoading}
              >
                {Array.isArray(jobCategories) &&
                  jobCategories.map((dept) => (
                    <option key={dept.uuid} value={dept.uuid}>
                      {dept.title} {typeof dept.job_count === "number" ? `(${dept.job_count})` : ""}
                    </option>
                  ))}
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Experience
              </FormLabel>
              <Select value={experience} onChange={handleExperienceChange} size="md" borderColor="gray.300">
                <option value="">All Experience</option>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((year) => (
                  <option key={year} value={year}>
                    {year} {year > 1 ? "Years" : "Year"}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Salary Range
              </FormLabel>
              <Select value={salaryRange} onChange={handleSalaryRangeChange} size="md" borderColor="gray.300">
                <option value="">All Salary Ranges</option>
                <option value="30000-300000">₹30K - ₹3L</option>
                <option value="300000-600000">₹3L - ₹6L</option>
                <option value="600000-1000000">₹6L - ₹10L</option>
                <option value="1000000-2000000">₹10L - ₹20L</option>
                <option value="2000000-10000000">Above ₹20L</option>
              </Select>
            </Box>

            <Box>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Location
              </FormLabel>
              <Select onChange={handleLocationSelect} size="md" borderColor="gray.300" placeholder="Select location">
                {Array.isArray(coreLocation) &&
                  coreLocation.map((item) => (
                    <option key={item.id || item.uuid} value={item.uuid}>
                      {item?.title}
                    </option>
                  ))}
              </Select>
            </Box>

            {(selectedSuggestions.length > 0 || locations.length > 0 || departmentUuid) && (
              <Box mt={2}>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Active Filters:
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  {selectedSuggestions.map((suggestion) => (
                    <Tag key={suggestion.uuid} size="md" borderRadius="full" variant="subtle" colorScheme={getSuggestionColorScheme(suggestion.uuid)} py={1} px={3}>
                      <TagLabel fontWeight="medium">
                        {suggestion?.title} ({getSuggestionType(suggestion.uuid)})
                      </TagLabel>
                      <TagCloseButton onClick={() => removeSuggestion(suggestion.uuid)} />
                    </Tag>
                  ))}
                  {locations.map((locUuid) => (
                    <Tag key={locUuid} size="md" borderRadius="full" variant="subtle" colorScheme="orange" py={1} px={3}>
                      <TagLabel fontWeight="medium">
                        {locationLabelMap[locUuid] ||
                          (Array.isArray(coreLocation) && coreLocation.find((loc) => loc.uuid === locUuid)?.title) ||
                          locUuid}
                      </TagLabel>
                      <TagCloseButton onClick={() => removeLocation(locUuid)} />
                    </Tag>
                  ))}

                  {departmentUuid && (
                    <Tag size="md" borderRadius="full" variant="subtle" colorScheme="teal" py={1} px={3}>
                      <TagLabel fontWeight="medium">
                        {(Array.isArray(jobCategories) && jobCategories.find((d) => d.uuid === departmentUuid)?.title) || "Department"}
                      </TagLabel>
                      <TagCloseButton onClick={removeDepartment} />
                    </Tag>
                  )}
                </Flex>
              </Box>
            )}

            {hasActiveFilters && (
              <Button mt={4} size="sm" variant="outline" colorScheme="blue" onClick={clearAllFilters} leftIcon={<FaFilter />}>
                Clear All Filters
              </Button>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Hero Section */}
      <Box className="bg-[linear-gradient(to_bottom_right,#1565C0,#1E88E5,_#26C6DA)]" py={{ base: 6, md: 8, lg: 10 }} mb={2}>
        <Box width="100%" mx="auto" px={{ base: 4, md: 6, lg: 8 }}>
          <VStack spacing={4} align="start">
            <Heading as="h1" fontSize={{ base: "2.25rem", md: "2.75rem" }} className="text-left" color={headerTextColor} fontWeight="700" lineHeight="1.2">
              Find Your Dream Job
            </Heading>
            <Text fontSize={{ base: "1rem", md: "1.125rem" }} color={subTextColor} maxW="800px" lineHeight="1.6" fontWeight="400">
              Discover thousands of job opportunities with advanced search and filtering capabilities.
            </Text>
          </VStack>

          <Box bg={cardBg} p={3} borderRadius="xl" boxShadow="sm">
            <VStack spacing={6}>
              <Flex gap={3} w="full" flexDirection={{ base: "column", md: "row" }}>
                <Box position="relative" flex={{ base: "auto", md: 2 }}>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Job title, skills, or company"
                      value={keyword}
                      onChange={handleChange}
                      onFocus={() => {
                        setIsInputFocused(true);
                        if (keyword.trim() && suggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      borderRadius="lg"
                      _focus={{ borderColor: "blue.500", boxShadow: "none" }}
                    />
                  </InputGroup>

                  {showSuggestions && suggestions.length > 0 && (
                    <Box
                      ref={suggestionsRef}
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      mt={1}
                      boxShadow="lg"
                      zIndex={10}
                      maxH="60"
                      overflowY="auto"
                    >
                      {suggestions.map((suggestion, index) => (
                        <Box
                          key={index}
                          px={4}
                          py={3}
                          _hover={{ bg: "gray.100" }}
                          cursor="pointer"
                          onClick={() => handleSuggestionClick(suggestion)}
                          borderBottom="1px"
                          borderColor="gray.100"
                          _last={{ borderBottom: "none" }}
                        >
                          <Flex justify="space-between" align="center">
                            <Text fontSize="sm" fontWeight="500">
                              {suggestion?.title}
                            </Text>
                            <Badge size="sm" colorScheme={getSuggestionColorScheme(suggestion.uuid)} variant="subtle">
                              {getSuggestionType(suggestion.uuid)}
                            </Badge>
                          </Flex>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <FormControl flex={{ base: "auto", md: 1 }}>
                  <Select
                    size="lg"
                    placeholder="Experience"
                    value={experience}
                    onChange={handleExperienceChange}
                    borderRadius="lg"
                    _focus={{ borderColor: "blue.500", boxShadow: "none" }}
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((year) => (
                      <option key={year} value={year}>
                        {year} {year > 1 ? "Years" : "Year"}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Location Dropdown with Search + Debounce */}
                <FormControl flex={{ base: "auto", md: 1 }}>
                  <Menu
                    isOpen={isLocMenuOpen}
                    onClose={() => {
                      setIsLocMenuOpen(false);
                      setLocationSearch("");
                    }}
                    closeOnSelect={false}
                  >
                    <MenuButton
                      as={Button}
                      size="lg"
                      borderRadius="lg"
                      variant="outline"
                      onClick={() => setIsLocMenuOpen((v) => !v)}
                      width="100%"
                      justifyContent="flex-start"
                      className="text-left "
                    >
                      {locations.length ? `Location (${locations.length} selected)` : "Location"}
                    </MenuButton>
                    <MenuList p={0} maxH="300px" overflowY="auto">
                      <Box p={3} borderBottom="1px solid" borderColor="gray.100" position="sticky" top="0" bg="white" zIndex={1}>
                        <InputGroup size="sm">
                          <InputLeftElement pointerEvents="none">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                          </InputLeftElement>
                          <Input
                            autoFocus
                            placeholder="Search location..."
                            value={locationSearch}
                            onChange={(e) => setLocationSearch(e.target.value)}
                          />
                          {coreLocationLoading && (
                            <InputRightElement>
                              <Spinner size="sm" />
                            </InputRightElement>
                          )}
                        </InputGroup>
                      </Box>

                      {Array.isArray(coreLocation) && coreLocation.length > 0 ? (
                        coreLocation.map((item) => (
                          <MenuItem
                            key={item.id || item.uuid}
                            onClick={() => {
                              handleLocationClick(item.uuid);
                              setIsLocMenuOpen(false);
                              setLocationSearch("");
                            }}
                          >
                            {item?.title}
                          </MenuItem>
                        ))
                      ) : (
                        <Box px={4} py={3} color="gray.500">
                          {coreLocationLoading ? (
                            <Flex align="center" gap={2}>
                              <Spinner size="sm" /> Loading...
                            </Flex>
                          ) : (
                            "No locations found"
                          )}
                        </Box>
                      )}
                    </MenuList>
                  </Menu>
                </FormControl>

                <Button
                  size="lg"
                  bg="blue.500"
                  color="white"
                  px={8}
                  _hover={{ bg: "blue.600" }}
                  onClick={handleSearchClick}
                  w={{ base: "full", md: "auto" }}
                >
                  Search
                </Button>
              </Flex>
            </VStack>
          </Box>

          {(selectedSuggestions.length > 0 || locations.length > 0 || departmentUuid) && (
            <Wrap spacing={3} mt={2} w="full">
              {selectedSuggestions.map((suggestion) => (
                <WrapItem key={suggestion.uuid}>
                  <Tag size="lg" colorScheme={getSuggestionColorScheme(suggestion.uuid)} borderRadius="full">
                    <TagLabel>
                      {suggestion?.title} ({getSuggestionType(suggestion.uuid)})
                    </TagLabel>
                    <TagCloseButton onClick={() => removeSuggestion(suggestion.uuid)} />
                  </Tag>
                </WrapItem>
              ))}

              {locations.map((locUuid) => (
                <WrapItem key={locUuid}>
                  <Tag size="lg" colorScheme="orange" borderRadius="full">
                    <TagLabel>
                      {locationLabelMap[locUuid] ||
                        (Array.isArray(coreLocation) && coreLocation.find((loc) => loc.uuid === locUuid)?.title) ||
                        locUuid}
                    </TagLabel>
                    <TagCloseButton onClick={() => removeLocation(locUuid)} />
                  </Tag>
                </WrapItem>
              ))}

              {departmentUuid && (
                <WrapItem>
                  <Tag size="lg" colorScheme="teal" borderRadius="full">
                    <TagLabel>
                      {(Array.isArray(jobCategories) && jobCategories.find((d) => d.uuid === departmentUuid)?.title) || "Department"}
                    </TagLabel>
                    <TagCloseButton onClick={removeDepartment} />
                  </Tag>
                </WrapItem>
              )}
            </Wrap>
          )}
        </Box>
      </Box>

      <Box width="100%" mx="auto" px={{ base: 4, md: 6, lg: 8 }} py={{ base: 4, md: 6, lg: 8 }}>
        {/* Mobile Filter Button */}
        <Box display={{ base: "block", lg: "none" }} mb={4}>
          <Button leftIcon={<FaFilter />} colorScheme="blue" variant="outline" onClick={onOpen} width="full">
            Filter Jobs
          </Button>
        </Box>

        {/* Mobile Filter Drawer */}
        <FilterDrawer />

        <Flex direction={{ base: "column", lg: "row" }} gap={6}>
          <Box width={{ base: "100%", lg: "300px" }} display={{ base: "none", lg: "block" }}>
            <FilterSidebar />
          </Box>

          <Box flex="1">
            {!isLoading && (
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="0.875rem" color="gray.600" fontWeight="400">
                  Showing {jobs?.length || 0} of {totalJobs || 0} jobs
                  {hasActiveFilters && " (filtered)"}
                </Text>
              </Flex>
            )}

            {(!hasActiveFilters || isInputFocused) && suggestions && suggestions.length > 0 && !showSuggestions && (
              <Box bg={cardBg} p={6} borderRadius="xl" mb={8} display={{ base: "none", md: "block" }}>
                <Heading as="h3" size="md" mb={4}>
                  Popular Searches:
                </Heading>
                <Wrap spacing={2}>
                  {suggestions.slice(0, 10).map((suggestion, index) => (
                    <WrapItem key={index}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSelect(suggestion)}
                        colorScheme={getSuggestionColorScheme(suggestion.uuid)}
                        rightIcon={<Badge size="xs" variant="subtle">{getSuggestionType(suggestion.uuid)}</Badge>}
                      >
                        {suggestion?.title}
                      </Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}

            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={3}>
              {renderJobContent()}
            </SimpleGrid>

            {!isLoading && Array.isArray(jobs) && jobs.length > 0 && (
              <Flex justify="center" mt={6} mb={4}>
                <div className="flex items-center mt-4 gap-4">
                  <button
                    className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    style={{ fontSize: "0.9rem", fontWeight: "500" }}
                  >
                    Previous
                  </button>
                  <span className="text-gray-700" style={{ fontSize: "0.9rem", fontWeight: "500" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    style={{ fontSize: "0.9rem", fontWeight: "500" }}
                  >
                    Next
                  </button>
                </div>
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}